import React, { useCallback, useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import moment from "moment-timezone"
import { Alert, Button, Container, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { OPEN_STATES, calendarReadPath, countryTimezone, monthOpened, readPlannerQuery, rememberPlannerSearch, rememberedPlannerSearch,
  toPublishPath, toSendPath, waitingPath, writePlannerQuery } from "./plannerQuery"
import { byStart, filterItems, itemsOf, movable, queueItems, withPublishing } from "./plannerItems"
import { addDays, applyMoves, eventMovedBy, newProposalDays, weeksMoved } from "./plannerCalendar"
import { moveWarningsText, todayIn } from "./plannerLabels"
import { EVENT_TOO_LATE, eventLockText } from "./proposalMessages"
import useCalendarRead from "./useCalendarRead"
import useMoveWarnings, { takesPartInWarnings } from "./useMoveWarnings"
import PlannerViewBar from "./PlannerViewBar"
import SeasonView from "./SeasonView"
import MonthView from "./MonthView"
import PlannerList from "./PlannerList"
import PlannerFilters from "./PlannerFilters"
import PlannerQueues from "./PlannerQueues"
import UsefulInformation from "./UsefulInformation"
import CalendarGaps, { gapsPath } from "./CalendarGaps"
import PlannerPeek from "./PlannerPeek"
import ProposalDrawer, { typing } from "./ProposalDrawer"
import AcknowledgeDialog from "./AcknowledgeDialog"
import MoveDialog from "./MoveDialog"
import EventMoveDialog from "./EventMoveDialog"
import HolidaysDialog from "./HolidaysDialog"
import PlannerToast from "./PlannerToast"
import ToSendTab from "./ToSendTab"
import WaitingTab from "./WaitingTab"
import ReadyToPublishTab from "./ReadyToPublishTab"
import PublishedProposal from "./PublishedProposal"
import usePlannerCommands from "./usePlannerCommands"
import "./eventsPlanner.scss"

// The tabs and their counts (B3 pinned decisions Q1–Q2).
const TAB_LABELS = [["calendar", "Calendar"], ["toSend", "To send"], ["waiting", "Waiting for answer"], ["toPublish", "Ready to publish"]]

// Events Planner (SuperAdmin only, cpAccessPolicy.js): the season planned as tournament proposals, with the published events
// (wayfinder tickets 13, 15, 16; PRD Module 8). The Calendar tab: Season, Month and List, the quick peek, moving proposals, holidays and
// the coherence warnings (Phases B3, C1), moving a published event until its registrations open (Phase E3); To send and Waiting for
// answer: sending each organizer's list and recording the answers (Phase C3).
export function EventsPlanner({ settings = {}, userDetails = {}, history, location }) {
  const userId = userDetails.id || userDetails._id
  const localToday = moment(Date.now()).format("YYYY-MM-DD")
  // Opened without a view (from the menu): the last one this user had, else the first opening's (B3 pinned decisions Q3, Q4).
  useEffect(() => {
    if (!location.search) history.replace({ pathname: location.pathname, search: writePlannerQuery(readPlannerQuery(rememberedPlannerSearch(userId), localToday)) })
  }, [history, location.pathname, location.search, localToday, userId])
  const query = useMemo(() => readPlannerQuery(location.search, localToday), [location.search, localToday])
  useEffect(() => { if (location.search) rememberPlannerSearch(userId, writePlannerQuery(query)) }, [location.search, query, userId])

  const timeZone = countryTimezone(query.country)
  const today = todayIn(timeZone)
  const calendar = useCalendarRead(location.search ? calendarReadPath(query) : null)
  // The queues of sending and answers (Phase C3), read on every tab for their counts: the organizers with something to send, and those
  // with proposals waiting for an answer (B3 pinned decision Q2). To send offers the new drafts of the season by its own months, or of
  // every upcoming date.
  const [sendPeriod, setSendPeriod] = useState("season")
  const toSend = useCalendarRead(location.search ? toSendPath(query, sendPeriod, today) : null, "To send could not be loaded.")
  const waiting = useCalendarRead(location.search ? waitingPath(query) : null, "Waiting for answer could not be loaded.")
  // The season's gaps (ticket 17, Phase D4), for the Calendar's "Gaps (n)".
  const gaps = useCalendarRead(location.search && query.tab === "calendar" ? gapsPath(query) : null, "The gaps could not be loaded.")
  // Ready to publish (Phase E2p): the country's confirmed proposals with their publication preview; the tab counts the complete ones.
  const toPublish = useCalendarRead(location.search ? toPublishPath(query.country) : null, "Ready to publish could not be loaded.")
  const counts = { toSend: toSend.data?.organizers.length, waiting: waiting.data?.organizers.length,
    toPublish: toPublish.data?.proposals.filter(proposal => proposal.complete).length }
  const [drawerVersion, setDrawerVersion] = useState(0)
  const commands = usePlannerCommands({ onDone: (action, proposal, result) => {
    calendar.reload()
    toSend.reload()
    waiting.reload()
    gaps.reload()
    toPublish.reload()
    if (action === "reopen" && result.status === "saved") setDrawerVersion(value => value + 1)
  } })
  const { tell } = commands
  // A moved proposal shows at its new dates until the calendar is read again.
  // A confirmed proposal carries what Ready to publish says of it (Phase E2p): its completeness for the queues and the List.
  const items = useMemo(() => withPublishing(applyMoves(itemsOf(calendar.data), commands.moves), toPublish.data).sort(byStart),
    [calendar.data, commands.moves, toPublish.data])
  const kept = useMemo(() => filterItems(items, query, today), [items, query, today])
  // One moment for the queues' counts and the queue listed, taken with each answer, so both agree at the 14-day edge.
  const now = useMemo(() => Date.now(), [calendar.data]) // eslint-disable-line react-hooks/exhaustive-deps
  const shown = useMemo(() => queueItems(kept, query.queue, now), [kept, query.queue, now])
  // The Season and Month views show the ended tournaments too (only the List hides them).
  const onCalendar = useMemo(() => queueItems(filterItems(items, query, ""), query.queue, now), [items, query, now])
  const inView = query.view === "list" ? shown : onCalendar
  // The ended tournaments the filters would otherwise show.
  const hiddenEnded = useMemo(() => filterItems(items, query, "").length - kept.length, [items, query, kept])

  // A change of the view is a new address (Back returns to the previous view); no state chosen is the default states. Another season
  // opens its Month view on its own month; a queue is listed (ticket 15 canvas).
  const update = changes => {
    const next = { ...query, ...changes }
    if (changes.season && !changes.month) next.month = monthOpened(next.season, today)
    if (changes.queue) next.view = "list"
    history.push({ pathname: location.pathname, search: writePlannerQuery({ ...next, states: next.states.length ? next.states : OPEN_STATES }) })
  }
  const reset = () => update({ categories: [], organizers: [], counties: [], regions: [], states: OPEN_STATES, published: true, warnings: false, queue: null })

  // The editor: a proposal of the view (Previous / Next follow its order), or a new one. A reopened proposal is read again.
  const [drawer, setDrawer] = useState(null)
  const [holidaysOpen, setHolidaysOpen] = useState(false)
  const listed = inView.filter(item => item.kind === "proposal")
  const position = drawer?.mode === "edit" ? listed.findIndex(item => item.id === drawer.id) : -1
  const row = drawer?.mode === "edit" ? items.find(item => item.kind === "proposal" && item.id === drawer.id) || null : null
  const openProposal = useCallback(item => setDrawer({ mode: "edit", id: item.id }), [])
  const close = useCallback(() => setDrawer(null), [])
  const created = proposal => {
    calendar.reload()
    if (!proposal) return close()
    // A Delete, Withdraw or move still waiting keeps its Undo.
    tell({ tone: "success", text: `${proposal.number} created as a draft.` })
    return setDrawer({ mode: "edit", id: proposal.id })
  }

  // The quick peek of a card or a chip, kept on the same tournament through each reread.
  const [peekKey, setPeekKey] = useState(null)
  const peeked = peekKey ? items.find(item => item.key === peekKey) || null : null
  const openPeek = useCallback(item => setPeekKey(item.key), [])
  const closePeek = useCallback(() => setPeekKey(null), [])
  // A warning's other tournament: its peek when the calendar holds it, else its editor or its event page.
  const openOther = other => {
    const found = items.find(item => item.kind === other.kind && item.id === other.id)
    if (found) return openPeek(found)
    return other.kind === "event" ? history.push(`/events/${other.id}`) : openProposal(other)
  }

  // A dialog keeps its tournament's key and reads it from the calendar as it is now (CP lesson 2026-09-30): an acknowledgement is sent
  // with the warning's current facts, a move at the current revision; when what it acts on is gone, it closes and says so.
  const [acknowledging, setAcknowledging] = useState(null)
  const acknowledgingItem = acknowledging ? items.find(item => item.key === acknowledging.itemKey) : null
  const acknowledgingWarning = acknowledgingItem?.warnings.find(warning => warning.key === acknowledging.warningKey) || null
  const warningGone = Boolean(acknowledging && calendar.data && !acknowledgingWarning)
  useEffect(() => {
    if (!warningGone) return
    setAcknowledging(null)
    tell({ tone: "info", text: "The warning you were acknowledging is gone: the facts behind it changed." })
  }, [warningGone, tell])
  const warningActions = (item, inPeek) => ({
    acknowledge: warning => setAcknowledging({ itemKey: item.key, warningKey: warning.key }),
    unacknowledge: warning => commands.unacknowledge(item, warning),
    ...(inPeek && { openOther }),
    ...(inPeek && OPEN_STATES.includes(item.state) && { withdraw: { label: `Withdraw ${item.number}`, run: () => { closePeek(); commands.withdraw(item) } } }),
  })

  // Moving (ticket 15 Q4, Q12–Q14): dates only, by days or whole weeks, sent after 7 s with Undo; a Confirmed proposal asks first, as
  // it returns to Proposed. Declined and withdrawn proposals are reopened first. A published event moves by whole weeks until its
  // registrations open, after its dialog (ticket 14, Phase E3).
  const [moving, setMoving] = useState(null)
  const movingItem = moving ? items.find(item => item.key === moving.key) : null
  const movingChanged = Boolean(moving && calendar.data && movingItem?.state !== "confirmed")
  useEffect(() => {
    if (!movingChanged) return
    setMoving(null)
    tell({ tone: "info", text: "This proposal changed meanwhile: move it again." })
  }, [movingChanged, tell])
  // A published event's move dialog reads the event as the calendar has it now; it closes when the event changed meanwhile (its dates, or
  // its registrations opened).
  const [eventMove, setEventMove] = useState(null)
  const eventMoveItem = eventMove ? items.find(item => item.key === eventMove.key) : null
  const eventMoveChanged = Boolean(eventMove && calendar.data
    && (!eventMoveItem || !movable(eventMoveItem) || eventMoveItem.datesRevision !== eventMove.datesRevision))
  useEffect(() => {
    if (!eventMoveChanged) return
    setEventMove(null)
    tell({ tone: "info", text: "This event changed meanwhile: move it again." })
  }, [eventMoveChanged, tell])
  // The warnings at a move's new dates (ticket 13 place 6, Phase C1w), from the server's engine: listed in "Move a confirmed proposal?",
  // named in the 7 s toast of a waiting move (the Month view's drop, Move ±1 week), and previewed while a Season chip is dragged; for a
  // published event, those its neighbours would get, in its move dialog (Phase E3).
  const moveWarnings = useMoveWarnings(calendar.data)
  const { load: loadMoveWarnings } = moveWarnings
  const movingStart = movingItem ? addDays(movingItem.days.start, moving.delta) : null
  useEffect(() => { if (movingItem) loadMoveWarnings(movingItem, [movingStart]) }, [loadMoveWarnings, movingItem, movingStart])
  const eventMoveStart = eventMoveItem ? addDays(eventMoveItem.days.start, 7 * eventMove.weeks) : null
  useEffect(() => { if (eventMoveItem) loadMoveWarnings(eventMoveItem, [eventMoveStart]) }, [loadMoveWarnings, eventMoveItem, eventMoveStart])
  const toastMove = commands.toast?.move
  useEffect(() => { if (toastMove) loadMoveWarnings(toastMove.item, [toastMove.start]) }, [loadMoveWarnings, toastMove])
  // A published event: whole weeks, to the same weekday of the week it lands in; refused here when its registrations are open or would
  // open in the past (Events checks both again).
  const requestEventMove = (item, delta) => {
    const weeks = weeksMoved(item, delta)
    if (!weeks) return undefined
    if (!movable(item)) return tell({ tone: "info", text: eventLockText(item) })
    if (Date.parse(eventMovedBy(item, weeks, timeZone).registrationsOpenAt) <= Date.now()) return tell({ tone: "info", text: EVENT_TOO_LATE })
    return setEventMove({ key: item.key, weeks, datesRevision: item.datesRevision })
  }
  const requestMove = (item, delta) => {
    if (!delta) return undefined
    if (item.kind === "event") return requestEventMove(item, delta)
    if (!movable(item)) return tell({ tone: "info", text: `Reopen ${item.number} as a draft to change it.` })
    if (item.state === "confirmed") return setMoving({ key: item.key, delta })
    return commands.move(item, delta, timeZone)
  }
  const refuseMove = item => tell({ tone: "info", text: `Not moved: the region and the organizer come from the ${item?.kind === "event" ? "event" : "proposal"}; `
    + "dragging changes only the dates." })
  // Ctrl+Z undoes the last move while it waits (ticket 15 Q14).
  const { undoMove } = commands
  useEffect(() => {
    const keys = event => {
      if ((event.ctrlKey || event.metaKey) && (event.key === "z" || event.key === "Z") && !typing(event.target) && undoMove()) event.preventDefault()
    }
    document.addEventListener("keydown", keys)
    return () => document.removeEventListener("keydown", keys)
  }, [undoMove])

  // "Send to organizer…" and a selection in the List (C3 pinned decision Q2): To send opens with those organizers included and exactly
  // those drafts ticked.
  // "Send the updated list" (Waiting for answer) opens it with that organizer included. The List's selection, by proposal id: what the
  // List shows of it is sent (Season and Month get no selection).
  const [selection, setSelection] = useState({})
  const chosen = shown.filter(item => item.kind === "proposal" && selection[item.id])
  const [sendFocus, setSendFocus] = useState(null)
  const focusUsed = useCallback(() => setSendFocus(null), [])
  const sendTo = (proposals, organizerIds = []) => {
    setDrawer(null)
    setPeekKey(null)
    setSendFocus({ key: `${Date.now()}-${[...organizerIds, ...proposals.map(({ id }) => id)].join(",")}`, ids: proposals.map(({ id }) => id),
      numbers: proposals.map(({ number }) => number), draftIds: proposals.filter(({ state }) => state === "draft").map(({ id }) => id),
      organizerIds: [...new Set([...organizerIds, ...proposals.map(({ organizer }) => organizer?.id).filter(Boolean)])] })
    update({ tab: "toSend" })
  }

  // "Publish…" (Phase E2p): a confirmed proposal's publish review, in Ready to publish. A published proposal opens as its header.
  const [publishFocus, setPublishFocus] = useState(null)
  const publishFocusUsed = useCallback(() => setPublishFocus(null), [])
  const [publishedId, setPublishedId] = useState(null)
  const [reviewVersion, setReviewVersion] = useState(0)
  const publishAction = proposal => proposal.state === "confirmed" && <button type="button" className="btn btn-light"
    onClick={() => { setDrawer(null); setPeekKey(null); setPublishFocus({ id: proposal.id, number: proposal.number }); update({ tab: "toPublish" }) }}>Publish…</button>
  // A proposal with an organizer is sent from To send (ticket 16): "Send to organizer…" replaces Mark as proposed.
  const sendAction = proposal => OPEN_STATES.includes(proposal.state) && proposal.organizer?.id
    && <button type="button" className="btn btn-light" onClick={() => sendTo([proposal])}>Send to organizer…</button>
  const stateActions = (proposal, busy) => {
    const later = command => () => { setDrawer(null); setPeekKey(null); command(proposal) }
    if (proposal.state === "draft" && !proposal.everProposed) {
      return <>{sendAction(proposal)}
        <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={later(commands.remove)}>Delete draft</button></>
    }
    if (["proposed", "confirmed", "draft"].includes(proposal.state)) {
      return <>{publishAction(proposal)}{sendAction(proposal)}
        <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={later(commands.withdraw)}>Withdraw</button></>
    }
    if (["refused", "withdrawn"].includes(proposal.state)) {
      return <button type="button" className="btn btn-light" disabled={busy || Boolean(commands.pending)} onClick={() => commands.reopen(proposal)}>Reopen as draft</button>
    }
    return null
  }

  return <div className="page-content events-planner">
    <Container fluid>
      <Breadcrumbs title="Events" path="/events-management" breadcrumbItem="Events Planner" />
      <div className="ep-root">
        <div className="ep-actions">
          {/* Generation (ticket 17, Phase D4) opens on the view's country and season. */}
          <Link className="btn btn-light" to={"/events-planner/generate?" + new URLSearchParams({ country: query.country, season: query.season,
            sport: query.sports.join(",") })}><i className="bx bx-bolt-circle" aria-hidden="true" /> Generate proposals</Link>
          <button type="button" className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
            <i className="bx bx-plus" aria-hidden="true" /> New proposal</button>
        </div>
        <div role="tablist" aria-label="Events Planner sections" className="ep-tabs">
          {TAB_LABELS.map(([key, label]) => <button type="button" role="tab" key={key} aria-selected={query.tab === key}
            className={"ep-tab" + (query.tab === key ? " is-selected" : "")} onClick={() => update({ tab: key })}>
            {label}{counts[key] !== undefined && <span className="ep-tab-count">{counts[key]}</span>}</button>)}
        </div>
        {query.tab === "calendar" && <>
          <PlannerQueues items={kept} queue={query.queue} now={now} publishing={Boolean(toPublish.data)} onChange={queue => update({ queue })} />
          <PlannerFilters query={query} items={items} today={today} settings={settings} onChange={update} onReset={reset} />
          <UsefulInformation userId={userId} />
          <CalendarGaps read={gaps} query={query} />
        </>}
        {query.tab === "calendar" && calendar.error && <Alert color="danger">{calendar.error}{" "}
          <Button color="link" className="p-0 align-baseline" onClick={calendar.reload}>Retry</Button></Alert>}
        {query.tab === "calendar" && !calendar.data && !calendar.error && <div className="ep-loading"><Spinner size="sm" /> Loading the calendar…</div>}
        {query.tab === "calendar" && calendar.data && <section className="ep-calendar" aria-label="Calendar">
          <PlannerViewBar query={query} today={today} onChange={update} onHolidays={() => setHolidaysOpen(true)} />
          {/* The Month view always shows its days: a click on one starts a proposal there. */}
          {query.view === "month" ? <MonthView items={onCalendar} query={query} holidays={calendar.data.holidays} settings={settings} onOpen={openPeek}
            onMove={requestMove} onCreate={day => setDrawer({ mode: "create", days: newProposalDays(day) })} />
          : inView.length ? <>
            {query.view === "season" && <SeasonView items={onCalendar} query={query} holidays={calendar.data.holidays} today={today} settings={settings}
              onOpen={openPeek} onShowMonth={month => update({ view: "month", month })} onMove={requestMove} onRefuse={refuseMove} moveWarnings={moveWarnings} />}
            {query.view === "list" && <>
              {chosen.length > 0 && <div className="ep-selection" role="region" aria-label="Selection">
                <b>{`${chosen.length} selected`}</b>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => { sendTo(chosen); setSelection({}) }}>Send to organizer…</button>
                <button type="button" className="btn btn-link btn-sm" onClick={() => setSelection({})}>Clear the selection</button>
              </div>}
              <PlannerList items={shown} hiddenEnded={hiddenEnded} settings={settings} timeZone={timeZone} onOpen={openProposal}
                viewKey={location.search} selected={selection}
                onSelect={item => setSelection(current => { const { [item.id]: was, ...rest } = current; return was ? rest : { ...current, [item.id]: true } })} />
            </>}
          </> : <div className="ep-card ep-empty">
            {items.length ? <>
              <b>No tournaments match these filters</b>
              <span className="ep-meta">Remove a filter or reset them all.</span>
              <button type="button" className="btn btn-light" onClick={reset}>Reset filters</button>
            </> : <b>No tournaments in this view yet.</b>}
          </div>}
        </section>}
        {query.tab === "toSend" && <ToSendTab query={query} read={toSend} period={sendPeriod} onPeriod={setSendPeriod} onCountry={country => update({ country })}
          settings={settings} timeZone={timeZone} focus={sendFocus} onFocusUsed={focusUsed} commands={commands} onOpenProposal={openProposal}
          onOpenWaiting={() => update({ tab: "waiting" })} />}
        {query.tab === "waiting" && <WaitingTab query={query} read={waiting} toSend={toSend.data?.organizers} onCountry={country => update({ country })}
          settings={settings} timeZone={timeZone}
          today={today} commands={commands} onOpenProposal={openProposal} onSendUpdated={organizerId => sendTo([], [organizerId])} />}
        {query.tab === "toPublish" && <ReadyToPublishTab key={query.country} query={query} read={toPublish} settings={settings} timeZone={timeZone}
          today={today} commands={commands} onCountry={country => update({ country })} onEditProposal={openProposal}
          onChanged={() => { calendar.reload(); toPublish.reload() }} onShowCalendar={() => update({ tab: "calendar" })}
          focus={publishFocus} onFocusUsed={publishFocusUsed} version={reviewVersion} />}
      </div>
      {peeked && !drawer && <PlannerPeek item={peeked} settings={settings} timeZone={timeZone} onClose={closePeek}
        onEdit={item => { closePeek(); openProposal(item) }} stateActions={peeked.kind === "proposal" ? stateActions(peeked, Boolean(commands.pending))
          : peeked.proposal && <button type="button" className="btn btn-light" onClick={() => { closePeek(); setPublishedId(peeked.proposal.id) }}>
            {`Proposal ${peeked.proposal.number}`}</button>}
        warningActions={peeked.kind === "proposal" ? warningActions(peeked, true) : undefined}
        moves={movable(peeked) && <span className="ep-peek-row">
          <button type="button" className="btn btn-light btn-sm" onClick={() => requestMove(peeked, -7)}>Move −1 week</button>
          <button type="button" className="btn btn-light btn-sm" onClick={() => requestMove(peeked, 7)}>Move +1 week</button>
        </span>} />}
      {drawer && <ProposalDrawer key={drawer.mode === "edit" ? `${drawer.id}-${drawerVersion}` : "new"} target={drawer} row={row} query={query}
        settings={settings} onClose={close} onCreated={created} actions={stateActions}
        onSaved={() => { calendar.reload(); toPublish.reload(); setReviewVersion(value => value + 1) }}
        onRecordAnswer={batch => { setDrawer(null); commands.answer(batch) }}
        warningActions={row ? warningActions(row, false) : undefined}
        onPrevious={position > 0 ? () => openProposal(listed[position - 1]) : null}
        onNext={position >= 0 && position < listed.length - 1 ? () => openProposal(listed[position + 1]) : null} />}
      {publishedId && <PublishedProposal id={publishedId} organizer={items.find(item => item.proposal?.id === publishedId)?.organizer} settings={settings}
        commands={commands} onClose={() => setPublishedId(null)} />}
      {holidaysOpen && calendar.data && <HolidaysDialog query={query} holidays={calendar.data.holidays} onClose={() => setHolidaysOpen(false)}
        onAdd={commands.addDayOff} onRemove={commands.removeDayOff} />}
      {movingItem?.state === "confirmed" && <MoveDialog item={movingItem} delta={moving.delta} timeZone={timeZone}
        newDates={moveWarnings.at(movingItem, movingStart)} onClose={() => setMoving(null)}
        onConfirm={() => { commands.move(movingItem, moving.delta, timeZone); setMoving(null) }} />}
      {eventMoveItem && !eventMoveChanged && <EventMoveDialog item={eventMoveItem} weeks={eventMove.weeks} timeZone={timeZone}
        neighbours={takesPartInWarnings(eventMoveItem)} newDates={moveWarnings.at(eventMoveItem, eventMoveStart)} onClose={() => setEventMove(null)}
        onConfirm={note => { commands.moveEvent(eventMoveItem, eventMove.weeks, note, timeZone); setEventMove(null) }} />}
      {acknowledgingWarning && <AcknowledgeDialog item={acknowledgingItem} warning={acknowledgingWarning} timeZone={timeZone}
        onClose={() => setAcknowledging(null)} onConfirm={note => commands.acknowledge(acknowledgingItem, acknowledgingWarning, note).then(result => {
          if (result?.status === "saved") setAcknowledging(null)
          return result
        })} />}
      <PlannerToast toast={commands.toast} detail={toastMove && moveWarningsText(moveWarnings.at(toastMove.item, toastMove.start))}
        onUndo={commands.undo} onDismiss={commands.dismiss} />
    </Container>
  </div>
}

export default connect(({ session: { settings, userDetails } = {} }) => ({ settings, userDetails }))(EventsPlanner)
